import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './Toast.module.css';

type Kind = 'success' | 'error' | 'info';
interface ToastItem { id: number; kind: Kind; message: string; }
interface TopItem { id: number; message: string; }
interface ToastApi {
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
  /**
   * Kurzer Hinweis OBEN mittig, verschwindet nach 2 s (Spec 2026-09-15 §7:
   * automatisches Übernehmen im Self-Service). Eigener Host, weil die unteren
   * Toasts weiter unten stapeln und 3 s stehen.
   */
  top: (m: string) => void;
}

const TOAST_MS = 3000;
const TOP_MS = 2000;

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [topItems, setTopItems] = useState<TopItem[]>([]);
  const nextId = useRef(1);
  const push = useCallback((kind: Kind, message: string) => {
    const id = nextId.current++;
    setItems((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), TOAST_MS);
  }, []);
  const pushTop = useCallback((message: string) => {
    const id = nextId.current++;
    setTopItems((prev) => [...prev, { id, message }]);
    setTimeout(() => setTopItems((prev) => prev.filter((t) => t.id !== id)), TOP_MS);
  }, []);
  const api: ToastApi = useMemo(() => ({
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
    top: (m) => pushTop(m),
  }), [push, pushTop]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.host}>
        {items.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles[t.kind]}`}>{t.message}</div>
        ))}
      </div>
      {/* role/aria-live am immer vorhandenen Host: Screenreader melden nur
          Aenderungen in einer Live-Region, die schon VOR dem Eintrag im DOM stand. */}
      <div className={styles.hostTop} role="status" aria-live="polite">
        {topItems.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles.info} ${styles.top}`}>{t.message}</div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast außerhalb des ToastProvider');
  return ctx;
}
